// src/hooks/useExaustor.js
/**
 * Hook monolítico para gerenciamento completo de Exaustores
 * Contém: fetch, sync, timer local, ações e timer padrão
 */

import React, { useState, useEffect, useCallback } from "react";
import getUnidadesExaustor from "../API/basic/Exaustor/listar";
import setTimer from "../API/basic/Exaustor/setTimer";
import { acionarExaustor, acionarUnidade } from "../API/basic/Exaustor/acionar";
import {
  parseTimeToSeconds,
  secondsToTimeString,
  shouldKeepExaustorOn,
  shouldTurnOffDueToTimer,
  hasActiveUnits,
} from "../utils/utilsExaustor";

const SYNC_INTERVAL = 4000; // 4 segundos
const TIMER_INTERVAL = 1000; // 1 segundo

/**
 * Hook principal para gerenciar estado e ações dos exaustores
 */
export const useExaustor = () => {
  const [exaustores, setExaustores] = useState([]);
  const [defaultTimerStr, setDefaultTimerStr] = useState("00:15:00");
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  
  // ✅ Flag para inicializar timer padrão apenas UMA VEZ
  const timerInitializedRef = React.useRef(false);

  // ==================== FETCH & SYNC ====================

  /**
   * Busca dados da API e sincroniza com estado local
   */
  const fetchAndSync = useCallback(async () => {
    const data = await getUnidadesExaustor();

    setExaustores((prev) => {
      const arr = Array.isArray(data) ? data : [];

      return arr.map((apiEx) => {
        const prevEx = prev.find((p) => p.id === apiEx.id);

        // ✅ Sincroniza timer com valor do backend
        // Se timer for negativo, mantém (indica modo dependente ou regra especial)
        const syncedTimer =
          typeof apiEx.timer === "number" && !Number.isNaN(apiEx.timer)
            ? apiEx.timer
            : prevEx?.timer ?? 0;

        // ✅ Confia no status do backend como fonte da verdade
        // Backend gerencia modo dependente, manual, etc
        return {
          ...apiEx,
          timer: syncedTimer,
        };
      });
    });

    // ✅ Inicializa input do timer padrão APENAS UMA VEZ (primeira requisição)
    if (!timerInitializedRef.current) {
      const any = Array.isArray(data) && data.length > 0 ? data[0] : null;
      if (any && typeof any.timerPadrao === "number") {
        setDefaultTimerStr(secondsToTimeString(any.timerPadrao));
        timerInitializedRef.current = true;
      }
    }
  }, []);

  // Sincronização automática a cada 7s
  useEffect(() => {
    fetchAndSync();
    const intervalId = setInterval(fetchAndSync, SYNC_INTERVAL);
    return () => clearInterval(intervalId);
  }, [fetchAndSync]);

  // ==================== TIMER LOCAL (FAKE CLOCK) ====================

  /**
   * Decrementa timers localmente a cada segundo
   */
  useEffect(() => {
    const interval = setInterval(() => {
      setExaustores((prev) =>
        prev.map((exaustor) => {
          // Se não está ligado, não decrementa
          if (exaustor.status !== "on") return exaustor;
          if (typeof exaustor.timer !== "number") return exaustor;

          // ✅ Timer negativo indica estado especial do backend (modo dependente, etc)
          // Não processa, deixa o backend gerenciar
          if (exaustor.timer < 0) return exaustor;

          // Timer maior que 1: decrementa
          if (exaustor.timer > 1) {
            return { ...exaustor, timer: exaustor.timer - 1 };
          }

          // Timer chegou a 0/1: verifica regras especiais
          if (exaustor.timer <= 1) {
            const hasShutdownCountdown =
              typeof exaustor.timerDesligamento === "number" &&
              exaustor.timerDesligamento > 0;

            // Permanece ligado se dependente ou tem countdown de desligamento
            if (exaustor.modoDependente || hasShutdownCountdown) {
              return {
                ...exaustor,
                status: "on",
                timer: 0,
                // Decrementa também o timerDesligamento (fake clock)
                timerDesligamento: hasShutdownCountdown
                  ? exaustor.timerDesligamento - 1
                  : exaustor.timerDesligamento,
              };
            }

            // Desliga exaustor e todas as unidades
            return {
              ...exaustor,
              status: "off",
              modoManual: false,
              timer: 0,
              unidades: (exaustor.unidades || []).map((u) => ({
                ...u,
                state: "off",
                timer: 0,
              })),
            };
          }

          return exaustor;
        })
      );
    }, TIMER_INTERVAL);

    return () => clearInterval(interval);
  }, []);

  // ==================== TIMER PADRÃO ====================

  /**
   * Aplica novo timer padrão no backend
   */
  const applyDefaultTimer = useCallback(async (seconds) => {
    setSaving(true);
    setSaveMsg(null);

    try {
      // ✅ Atualiza IMEDIATAMENTE o estado local
      setExaustores((prev) =>
        prev.map((ex) => ({ ...ex, timerPadrao: seconds }))
      );
      
      // Salva no backend
      await setTimer(seconds);
      
      setSaveMsg("Timer padrão atualizado.");
    } catch (err) {
      console.error("Erro ao salvar timer padrão:", err);
      setSaveMsg("Falha ao salvar o timer.");
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMsg(null), 3000);
    }
  }, []);

  /**
   * Handler para salvar timer padrão
   */
  const handleSaveDefaultTimer = useCallback(() => {
    const secs = parseTimeToSeconds(defaultTimerStr);
    
    if (secs === null) {
      setSaveMsg("Formato inválido. Use HH:MM:SS, MM:SS ou SS.");
      return;
    }
    
    applyDefaultTimer(secs);
  }, [defaultTimerStr, applyDefaultTimer]);

  // ==================== AÇÕES ====================

  /**
   * Envia comando para backend (acionamento manual)
   */
  const sendCommand = useCallback((exaustor) => {
    acionarExaustor(exaustor);
  }, []);

  /**
   * Liga/desliga exaustor manualmente (modo MANUAL via PC)
   */
  const toggleExaustor = useCallback(
    (exaustorIdStr) => {
      const exaustorAtual = exaustores.find((e) => e.id === exaustorIdStr);
      if (exaustorAtual) sendCommand(exaustorAtual);

      setExaustores((prev) =>
        prev.map((exaustor) => {
          if (exaustor.id !== exaustorIdStr) return exaustor;

          const turningOn = exaustor.status === "off";
          const newStatus = turningOn ? "on" : "off";

          // Reset de unidades ao entrar em manual
          const unidadesReset = (exaustor.unidades || []).map((u) => ({
            ...u,
            state: "off",
            timer: 0,
          }));

          return {
            ...exaustor,
            status: newStatus,
            modoManual: turningOn ? "ManualPC" : false,
            // Ao LIGAR, reseta timer com timerPadrao
            timer: turningOn
              ? typeof exaustor.timerPadrao === "number"
                ? exaustor.timerPadrao
                : 0
              : 0,
            unidades: unidadesReset,
          };
        })
      );
    },
    [exaustores, sendCommand]
  );

  /**
   * Liga/desliga unidade individual (modo UNIDADE)
   */
  const toggleUnidade = useCallback(
    (exaustorIdStr, unidadeIdNum) => {
      const exaustorAtual = exaustores.find((e) => e.id === exaustorIdStr);
      if (exaustorAtual) acionarUnidade(exaustorAtual, Number(unidadeIdNum));

      setExaustores((prev) =>
        prev.map((exaustor) => {
          if (exaustor.id !== exaustorIdStr) return exaustor;

          // Atualiza estado da unidade clicada
          const updatedUnidades = (exaustor.unidades || []).map((unidade) => {
            if (unidade.id === unidadeIdNum) {
              const newState = unidade.state === "running" ? "off" : "running";
              return { ...unidade, state: newState };
            }
            return unidade;
          });

          // Detecta se UMA NOVA unidade foi acionada (para reset de timer)
          const antes = exaustor.unidades?.find((u) => u.id === unidadeIdNum)?.state;
          const depois = updatedUnidades.find((u) => u.id === unidadeIdNum)?.state;
          const novaUnidadeAcionada = antes !== "running" && depois === "running";

          // Verifica se alguma unidade ficou ativa
          const algumaAtiva = hasActiveUnits(updatedUnidades);

          return {
            ...exaustor,
            unidades: updatedUnidades,
            // Status considera modo dependente
            status: algumaAtiva || exaustor.modoDependente ? "on" : "off",
            modoManual: false, // Sai do modo manual
            // Reseta timer do EXAUSTOR ao acionar nova unidade
            timer: novaUnidadeAcionada
              ? typeof exaustor.timerPadrao === "number"
                ? exaustor.timerPadrao
                : 0
              : exaustor.timer,
          };
        })
      );

      // Garante timer 0 se nenhuma unidade ficou ativa
      setExaustores((prev) =>
        prev.map((exaustor) => {
          if (exaustor.id !== exaustorIdStr) return exaustor;
          const algumaAtiva = hasActiveUnits(exaustor.unidades || []);
          return algumaAtiva ? exaustor : { ...exaustor, timer: 0 };
        })
      );
    },
    [exaustores]
  );

  // ==================== RETORNO ====================

  return {
    // Estado
    exaustores,
    setExaustores,
    
    // Timer Padrão
    defaultTimerStr,
    setDefaultTimerStr,
    saving,
    saveMsg,
    handleSaveDefaultTimer,
    
    // Ações
    toggleExaustor,
    toggleUnidade,
    
    // Refresh manual (se necessário)
    refresh: fetchAndSync,
  };
};

export default useExaustor;